import { CheckCircle } from "lucide-react";
import { useRegistrationConfig } from '../../../Contexts/RegistrationConfigContext';

const Overview = ({ event }) => {
  const { registrationDeadline: configDeadline } = useRegistrationConfig();
  const displayDeadline = configDeadline || event.deadline;

  // Helper to format Firestore Timestamp, Date, or string
  const formatDeadline = (deadline) => {
    if (!deadline) return '';
    // Firestore Timestamp object
    if (deadline.toDate && typeof deadline.toDate === 'function') {
      return deadline.toDate().toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    }
    // Firestore Timestamp plain object
    if (deadline.seconds) {
      return new Date(deadline.seconds * 1000).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    }
    // JS Date
    if (deadline instanceof Date) {
      return deadline.toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    }
    // String
    if (typeof deadline === 'string') {
      const date = new Date(deadline);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric', month: 'long', day: 'numeric'
        });
      }
      return deadline;
    }
    // Fallback
    return String(deadline);
  };

  return (
  <div className="space-y-6 sm:space-y-12">
    {/* Event Header */}
    <div className="grid md:grid-cols-3 gap-4 sm:gap-8 mb-6 sm:mb-12">
      <div className="md:col-span-2">
        <h1 className="text-xl sm:text-3xl font-bold text-gray-800 mb-2 sm:mb-4">
          {event.title}
        </h1>
        <p className="text-sm sm:text-lg text-gray-600 mb-4 sm:mb-6 leading-relaxed">
          {event.description}
        </p>
        <div className="grid grid-cols-2 gap-3 sm:gap-6">
          <div className="bg-gray-50 rounded-lg p-2 sm:p-4">
            <div className="text-xs sm:text-sm text-gray-500 mb-1">Date</div>
            <div className="font-semibold text-custom-blue-1 text-xs sm:text-base">
              {event.date}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-2 sm:p-4">
            <div className="text-xs sm:text-sm text-gray-500 mb-1">
              Location
            </div>
            <div className="font-semibold text-custom-blue-1 text-xs sm:text-base">
              {event.location}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-2 sm:p-4">
            <div className="text-xs sm:text-sm text-gray-500 mb-1">Prizes</div>
            <div className="font-semibold text-custom-green-1 text-xs sm:text-base">
              {event.prizes}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-2 sm:p-4">
            <div className="text-xs sm:text-sm text-gray-500 mb-1">
              Deadline
            </div>
            <div className="font-semibold text-red-500 text-xs sm:text-base">
              {formatDeadline(displayDeadline)}
            </div>
          </div>
        </div>
      </div>
      <div className="space-y-4">
        <div className="flex justify-center">
          <img
            src={event.image || "/Images/background.jpg"}
            alt={event.title}
            className="w-full h-32 sm:h-48 object-cover rounded-xl shadow-md"
          />
        </div>

        {/* Partners/Sponsors Logos - Below Image */}
        <div className="flex justify-center items-center gap-3 sm:gap-4">
          {/* ABQ Logo */}

          <div className="bg-white rounded-full p-3 shadow-md hover:shadow-lg transition-shadow border border-gray-200 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center">
            <img
              src="/Images/sti.png"
              alt="STI"
              className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
            />
          </div>
          <div className="bg-white rounded-full p-3 shadow-md hover:shadow-lg transition-shadow border border-gray-200 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center">
            <img
              src="/Images/abq.png"
              alt="ABQ"
              className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
            />
          </div>

          {/* STI Logo */}

          {/* EDIC Logo */}
          <div className="bg-white rounded-full p-3 shadow-md hover:shadow-lg transition-shadow border border-gray-200 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center">
            <img
              src="/Images/edic.png"
              alt="EDIC"
              className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
            />
          </div>
        </div>
      </div>
    </div>

    {/* Separator Line */}
    <div className="border-t border-gray-300 my-8 sm:my-16"></div>

    {/* Detailed Description */}
    <div className="mb-6 sm:mb-12">
      <h2 className="text-lg sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-6">
        About This Hackathon
      </h2>
      <div className="grid md:grid-cols-2 gap-4 sm:gap-8 items-start">
        {/* Left Column - Main Content */}
        <div className="space-y-4 sm:space-y-8">
          {/* Brief Introduction */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-3 sm:p-6 border border-gray-200">
            <h3 className="text-base sm:text-xl font-bold text-gray-800 mb-2 sm:mb-4">
              🚀 Innovation Sprint
            </h3>
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
              Join us for the 1st Industry 4.0+ Hackathon, a 72-hour intensive
              innovation sprint focused on developing AI and hardware solutions
              that leverage Uganda's ABQ Sovereign Cloud Platform.
            </p>
          </div>

          {/* Challenge Categories Cards */}
          <div>
            <h3 className="text-base sm:text-xl font-bold text-gray-800 mb-2 sm:mb-4">
              Challenge Categories
            </h3>
            <div className="grid gap-2 sm:gap-4">
              {/* Agriculture Card */}
              <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-200 hover:shadow-md transition-shadow hover:border-custom-blue-1">
                <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-custom-blue-1 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs sm:text-sm font-bold">
                      🌾
                    </span>
                  </div>
                  <h4 className="font-bold text-gray-800 text-sm sm:text-base">
                    AGRICULTURE: Smart Farming & Market Access
                  </h4>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 pl-8 sm:pl-11">
                  Transform farming with AI software and IoT for crop
                  monitoring, disease detection, and market connectivity.
                </p>
              </div>

              {/* Tourism Card */}
              <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-200 hover:shadow-md transition-shadow hover:border-custom-blue-1">
                <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-custom-blue-1 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs sm:text-sm font-bold">
                      🏞️
                    </span>
                  </div>
                  <h4 className="font-bold text-gray-800 text-sm sm:text-base">
                    TOURISM: Intelligent Visitor Experiences
                  </h4>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 pl-8 sm:pl-11">
                  Enrich Uganda's tourism with AI-powered guides, cultural
                  experiences, and sustainable eco-tourism platforms.
                </p>
              </div>

              {/* Mining Card */}
              <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-200 hover:shadow-md transition-shadow hover:border-custom-blue-1">
                <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-custom-blue-1 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs sm:text-sm font-bold">
                      ⛏️
                    </span>
                  </div>
                  <h4 className="font-bold text-gray-800 text-sm sm:text-base">
                    MINING: Safety, Efficiency & Transparency
                  </h4>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 pl-8 sm:pl-11">
                  Enhance mining safety and efficiency with IoT systems, AI
                  analysis, and blockchain transparency.
                </p>
              </div>

              {/* STI Card */}
              <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-200 hover:shadow-md transition-shadow hover:border-custom-blue-1">
                <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-custom-blue-1 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs sm:text-sm font-bold">
                      🔬
                    </span>
                  </div>
                  <h4 className="font-bold text-gray-800 text-sm sm:text-base">
                    STI: Innovation & Research Infrastructure
                  </h4>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 pl-8 sm:pl-11">
                  Build the next-generation homegrown social media platform
                  powered by Uganda’s Sovereign Cloud and supported by national
                  innovation infrastructure. The challenge is to design a
                  platform that not only connects people but also serves African
                  realities — with features that promote local content,
                  languages, cultural identity, and secure data sovereignty.
                </p>
              </div>
            </div>
          </div>

          {/* Important Note */}
          <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-3 sm:p-6">
            <h4 className="font-bold text-red-800 text-sm sm:text-lg mb-2 sm:mb-3">
              💡 IMPORTANT NOTE
            </h4>
            <div className="space-y-1 sm:space-y-2 text-red-700">
              <p className="font-semibold text-xs sm:text-base">
                These categories are GUIDING THEMES, not fixed problem
                statements.
              </p>
              <p className="text-xs sm:text-base">
                Teams are FREE to define their own problems and design
                innovative solutions within these areas!
              </p>
            </div>
          </div>

          {/* Infrastructure Highlight */}
          <div className="bg-gray-50 rounded-xl p-3 sm:p-6 border border-gray-200">
            <h4 className="font-bold text-gray-800 mb-2 sm:mb-3 text-sm sm:text-base">
              🛠️ Cutting-Edge Infrastructure
            </h4>
            <p className="text-gray-700 text-xs sm:text-sm leading-relaxed">
              Access Uganda's Sovereign Cloud Platform, IoT development tools,
              AI/ML frameworks, and hardware prototyping facilities at EDIC,
              with mentorship from leading researchers and entrepreneurs.
            </p>
          </div>
        </div>

        {/* Right Column - Video and Organization Info */}
        <div className="space-y-6 sm:space-y-12">
          {/* Video Card */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-3 sm:p-6 shadow-lg border border-gray-200">
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-4">
              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-red-500 rounded-full"></div>
              <h3 className="text-sm sm:text-lg font-semibold text-gray-800">
                Deep Tech Center of Excellence Launch
              </h3>
            </div>

            <div className="relative w-full aspect-video bg-gray-900 rounded-lg overflow-hidden shadow-md">
              <iframe
                className="w-full h-full"
                src="https://www.youtube-nocookie.com/embed/3JXj16Xvpg8?rel=0&modestbranding=1&showinfo=0&controls=1&fs=1&cc_load_policy=0&iv_load_policy=3&autohide=0"
                title="Deep Tech Center of Excellence Launch"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              ></iframe>
            </div>
          </div>

          {/* Organization Information */}
          <div className="bg-white rounded-xl p-3 sm:p-6 shadow-lg border border-gray-200">
            <h3 className="text-sm sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-6">
              About the Organizers
            </h3>

            <div className="space-y-3 sm:space-y-6">
              {/* STI Secretariat */}
              <div className="border-l-4 border-custom-blue-1 pl-2 sm:pl-4">
                <h4 className="font-bold text-gray-800 mb-1 sm:mb-2 text-sm sm:text-base">
                  🏛️ Science, Technology and Innovations Secretariat
                </h4>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-2 sm:mb-3">
                  We mobilize, coordinate and provide strategic oversight and
                  policy guidance to scientists and stakeholders along
                  prioritized industrial value chains.
                </p>
                <div className="bg-blue-50 rounded-lg p-2 sm:p-3">
                  <p className="text-xs font-semibold text-blue-800">Vision:</p>
                  <p className="text-xs text-blue-700">
                    Uganda as the best, most technologically advanced and most
                    innovative nation in the region.
                  </p>
                </div>
              </div>

              {/* Industry 4.0+ Bureau */}
              <div className="border-l-4 border-custom-green-1 pl-2 sm:pl-4">
                <h4 className="font-bold text-gray-800 mb-1 sm:mb-2 text-sm sm:text-base">
                  🚀 Industry 4.0+
                </h4>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-2 sm:mb-3">
                  Our major focus is on research, development, technology
                  transfer, and commercialization of innovations in high-tech
                  sectors including smart electronics, AI, cybersecurity,
                  enterprise software, cloud computing and additive
                  manufacturing.
                </p>
                <div className="bg-green-50 rounded-lg p-2 sm:p-3">
                  <p className="text-xs font-semibold text-green-800">Goal:</p>
                  <p className="text-xs text-green-700">
                    Establishment of a vibrant, self-sustaining electronics and
                    high-tech ecosystem contributing 2.7% of Uganda's GDP by
                    2040.
                  </p>
                </div>
              </div>

              {/* Deep Tech Center */}
              <div className="border-l-4 border-red-500 pl-2 sm:pl-4">
                <h4 className="font-bold text-gray-800 mb-1 sm:mb-2 text-sm sm:text-base">
                  🔬 Deep Tech Center of Excellence
                </h4>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-2 sm:mb-3">
                  A state-of-the-art facility fostering innovation in AI,
                  Machine Learning, IoT, Robotics, Biotechnology, Advanced
                  Materials, Cloud Computing, Edge Computing, and Quantum
                  Computing.
                </p>
                <div className="bg-red-50 rounded-lg p-2 sm:p-3">
                  <p className="text-xs font-semibold text-red-800">
                    Key Features:
                  </p>
                  <p className="text-xs text-red-700">
                    Local Electronics Manufacturing Facility, AI Studio for
                    government agencies, Deep Tech Venture Studio, and local
                    data repository programs.
                  </p>
                </div>
              </div>
            </div>

            {/* Partnership Note */}
            <div className="mt-3 sm:mt-6 bg-gray-50 rounded-lg p-2 sm:p-4">
              <p className="text-xs text-gray-500 text-center">
                This hackathon represents a collaborative effort to showcase
                Uganda's growing innovation ecosystem and technical capabilities
                on the global stage.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Separator Line */}
    <div className="border-t border-gray-300 my-16"></div>

    {/* Overview Details */}
    {event.overview && (
      <>
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Event Overview
            </h3>
            <div className="space-y-4">
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">
                  Theme
                </div>
                <div className="text-gray-800">{event.overview.theme}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">
                  Format
                </div>
                <div className="text-gray-800">{event.overview.format}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">
                  Target Audience
                </div>
                <div className="text-gray-800">{event.overview.audience}</div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Eligibility
            </h3>
            <div className="space-y-3">
              {event.requirements?.map((req, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-custom-green-1 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{req}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Separator Line */}
        <div className="border-t border-gray-300 my-16"></div>
      </>
    )}

    {/* Work Plan */}
    {event.workPlan && (
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-6">PROGRAM</h3>
        <div className="grid md:grid-cols-2 gap-6">
          {Object.entries(event.workPlan).map(([key, phase], index) => (
            <div
              key={key}
              className="bg-gray-50 rounded-xl p-6 space-y-4 border border-gray-200 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between border-b border-gray-200 pb-3">
                <h4 className="font-bold text-custom-blue-1 text-lg">
                  {phase.phase}
                </h4>
                <span className="bg-custom-blue-1 text-white px-3 py-1 rounded-full text-sm font-medium">
                  {phase.timeline}
                </span>
              </div>

              <div className="space-y-3">
                <div>
                  <h5 className="font-semibold text-gray-700 mb-2">
                    Activities
                  </h5>
                  <div className="space-y-2">
                    {phase.activities.map((activity, actIndex) => (
                      <div key={actIndex} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-custom-green-1 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">
                          {activity}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3">
                  <h5 className="font-semibold text-gray-700 mb-1">
                    Responsible
                  </h5>
                  <p className="text-sm text-gray-600">{phase.responsible}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
  );
};

export default Overview;
